/**
 * Student dormitory v2 API (`/rest/dormitory/v2/...`).
 */
import axios from 'axios';
import { http, defaultConfig, getUniversityUrl } from './http';
import { parseError } from '@/utils';
import { loginToken } from './user.service';
import { openPreRegistrationPaymentPage } from './payment.service';

const DORMITORY_V2 = '/rest/dormitory/v2';

async function withParsedError<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  }
  catch (e) {
    throw new Error(parseError(e));
  }
}

export function fetchStudentPreRegistrationSchedule() {
  return (async () => {
    try {
      const baseURL = getUniversityUrl();
      const url = `${baseURL}${DORMITORY_V2}/scheduling/pre-registration-event/available`;
      const { data } = await http.get<HttpResponse<DormitoryStudentPreRegistrationEvent>>(url, defaultConfig(baseURL));
      return data.payload ?? null;
    }
    catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 404) {
        return null;
      }
      throw new Error(parseError(e));
    }
  })();
}

export function fetchMyStudentPreRegistrations() {
  return (async () => {
    try {
      const baseURL = getUniversityUrl();
      const url = `${baseURL}${DORMITORY_V2}/pre-registration/my`;
      const { data } = await http.get<HttpResponse<HttpResponsePayload<StudentPreRegistrationMeEntity[]>>>(url, defaultConfig(baseURL));
      return data.payload?.content ?? [];
    }
    catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 404) {
        return [];
      }
      throw new Error(parseError(e));
    }
  })();
}

export type StudentPreRegistrationCreatePayload = {
  acceptedRules: boolean;
  preRegistrationEventId: number;
  suggestedDormitoryId?: number | null;
};

export function submitStudentPreRegistration(payload: StudentPreRegistrationCreatePayload) {
  return withParsedError(async () => {
    const baseURL = getUniversityUrl();
    const url = `${baseURL}${DORMITORY_V2}/pre-registration`;
    const body: StudentPreRegistrationCreatePayload = {
      acceptedRules: payload.acceptedRules,
      preRegistrationEventId: payload.preRegistrationEventId,
      suggestedDormitoryId: payload.suggestedDormitoryId
    };
    if (payload.suggestedDormitoryId != null) {
      body.suggestedDormitoryId = payload.suggestedDormitoryId;
    }
    return http.post(url, body, defaultConfig(baseURL));
  });
}

/** Active schedule when within `startDate`…`endDate` and not hidden from users. */
export function getActiveStudentPreRegistrationSchedule(
  schedule: DormitoryStudentPreRegistrationEvent | null,
  nowMs: number = Date.now(),
): DormitoryStudentPreRegistrationEvent | null {
  if (schedule == null) {
    return null;
  }
  if (schedule.hideFromUsers === true) {
    return null;
  }
  const start = new Date(schedule.startDate).getTime();
  const end = new Date(schedule.endDate).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end)) {
    return null;
  }
  if (nowMs < start || nowMs > end) {
    return null;
  }
  return schedule;
}

/** Fee in rials (or same unit as API); dorm row overrides default when selected. */
export function resolveStudentPreRegistrationFee(
  event: DormitoryStudentPreRegistrationEvent,
  suggestedDormitoryId?: number | null,
): number {
  if (suggestedDormitoryId != null) {
    const row = event.dormitoryCapacities?.find(r => r.dormitoryId === suggestedDormitoryId);
    if (row?.preRegistrationFee != null && Number.isFinite(row.preRegistrationFee)) {
      return row.preRegistrationFee;
    }
  }
  const fallback = event.defaultPreRegistrationFee ?? 0;
  return Number.isFinite(fallback) ? fallback : 0;
}

export function requiresPreRegistrationPayment(
  event: DormitoryStudentPreRegistrationEvent,
  suggestedDormitoryId?: number | null,
): boolean {
  return resolveStudentPreRegistrationFee(event, suggestedDormitoryId) > 0;
}

/** Clears legacy session payload from older paid-flow builds (no longer written). */
export function clearPreRegistrationPaymentContext() {
  sessionStorage.removeItem('dormitory-v2-pre-reg-payment-context');
}

/** Legacy Rose payment bridge after `login-token` (same pattern as wallet charge). */
export async function redirectToPreRegistrationPaymentPage() {
  const response = await loginToken();
  const token = response.data?.payload?.loginAsToken;
  if (!token) {
    throw new Error('login-token');
  }
  openPreRegistrationPaymentPage(token);
}
